import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Card, 
  Select, 
  Input, 
  Tag, 
  Space, 
  Empty,
  Timeline,
  Button,
} from 'antd';
import { 
  CalendarOutlined, 
  SearchOutlined, 
  FilterOutlined,
  FileTextOutlined,
  TagOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import ArchiveItem from '../../components/ArchiveItem';
import { posts, getArchives } from '../../api/mockData';
import './index.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const ArchivePage = () => {
  const [archives, setArchives] = useState([]);
  const [filteredArchives, setFilteredArchives] = useState([]);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [years, setYears] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'timeline'

  useEffect(() => {
    // 获取归档数据
    const archiveData = getArchives();
    setArchives(archiveData);
    setFilteredArchives(archiveData);
    
    // 提取所有标签
    const tags = [...new Set(posts.flatMap(post => post.tags))];
    setAllTags(tags);
    
    // 提取所有年份
    const uniqueYears = [...new Set(archiveData.map(a => a.year))].sort((a, b) => b - a);
    setYears(uniqueYears);
  }, []);

  useEffect(() => {
    // 应用过滤器
    let filtered = archives;
    
    if (selectedYear !== 'all') {
      filtered = filtered.filter(archive => archive.year === parseInt(selectedYear));
    }
    
    if (selectedMonth !== 'all' && selectedYear !== 'all') {
      filtered = filtered.filter(archive => archive.month === parseInt(selectedMonth));
    }
    
    if (selectedTag !== 'all') {
      filtered = filtered.map(archive => ({
        ...archive,
        posts: archive.posts.filter(post => post.tags.includes(selectedTag))
      })).filter(archive => archive.posts.length > 0);
    }
    
    if (searchQuery.trim()) {
      filtered = filtered.map(archive => ({
        ...archive,
        posts: archive.posts.filter(post => 
          post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.summary.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(archive => archive.posts.length > 0);
    }
    
    setFilteredArchives(filtered);
  }, [selectedYear, selectedMonth, selectedTag, searchQuery, archives]);

  const getTotalStats = () => {
    return {
      totalPosts: posts.length,
      totalArchives: archives.length,
      totalTags: allTags.length,
      earliestPost: archives.length > 0 
        ? Math.min(...archives.map(a => a.year))
        : new Date().getFullYear()
    };
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleClearFilters = () => {
    setSelectedYear('all');
    setSelectedMonth('all');
    setSelectedTag('all');
    setSearchQuery('');
  };

  const stats = getTotalStats();
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <Layout className="archive-page">
      <Content className="archive-container">
        {/* 页头 */}
        <div className="archive-header">
          <Card className="archive-hero">
            <Title level={2} className="archive-title">
              <CalendarOutlined /> 文章归档
            </Title>
            <Text className="archive-subtitle">
              按时间轴整理所有文章，方便查阅和回顾。共收录 {stats.totalPosts} 篇文章，涵盖 {stats.totalTags} 个主题标签。
            </Text>
            
            <div className="archive-stats">
              <div className="stat-item">
                <div className="stat-number">{stats.totalPosts}</div>
                <Text className="stat-label">总文章数</Text>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.totalArchives}</div>
                <Text className="stat-label">归档月份</Text>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.totalTags}</div>
                <Text className="stat-label">标签数量</Text>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.earliestPost}</div>
                <Text className="stat-label">始于年份</Text>
              </div>
            </div>
          </Card>
        </div>

        {/* 过滤器和内容 */}
        <Card className="archive-content">
          {/* 过滤器 */}
          <div className="archive-filters">
            <div className="filter-row">
              <Select
                className="filter-item year-select"
                placeholder="选择年份"
                value={selectedYear}
                onChange={setSelectedYear}
                allowClear
              >
                <Option value="all">所有年份</Option>
                {years.map(year => (
                  <Option key={year} value={year}>{year}</Option>
                ))}
              </Select>
              
              <Select
                className="filter-item month-select"
                placeholder="选择月份"
                value={selectedMonth}
                onChange={setSelectedMonth}
                disabled={selectedYear === 'all'}
                allowClear
              >
                <Option value="all">所有月份</Option>
                {monthOptions.map(month => (
                  <Option key={month} value={month}>{month}月</Option>
                ))}
              </Select>
              
              <Select
                className="filter-item tag-filter"
                placeholder="选择标签"
                value={selectedTag}
                onChange={setSelectedTag}
                allowClear
              >
                <Option value="all">所有标签</Option>
                {allTags.map(tag => (
                  <Option key={tag} value={tag}>
                    <TagOutlined /> {tag}
                  </Option>
                ))}
              </Select>
              
              <Search
                className="filter-item search-filter"
                placeholder="搜索文章..."
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onSearch={handleSearch}
                allowClear
              />
            </div>
            
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <FilterOutlined />
                <Text type="secondary">
                  当前筛选: 
                  {selectedYear !== 'all' && ` 年份: ${selectedYear}`}
                  {selectedMonth !== 'all' && ` 月份: ${selectedMonth}`}
                  {selectedTag !== 'all' && ` 标签: ${selectedTag}`}
                  {searchQuery && ` 搜索: ${searchQuery}`}
                  {(selectedYear === 'all' && selectedMonth === 'all' && selectedTag === 'all' && !searchQuery) && ' 全部文章'}
                </Text>
              </Space>
              
              <Space>
                <Button 
                  type={viewMode === 'list' ? 'primary' : 'default'}
                  onClick={() => setViewMode('list')}
                  icon={<FileTextOutlined />}
                >
                  列表视图
                </Button>
                <Button 
                  type={viewMode === 'timeline' ? 'primary' : 'default'}
                  onClick={() => setViewMode('timeline')}
                  icon={<ClockCircleOutlined />}
                >
                  时间轴
                </Button>
                <Button onClick={handleClearFilters}>
                  清除筛选
                </Button>
              </Space>
            </div>
          </div>

          {/* 归档内容 */}
          <div className="archive-list">
            {filteredArchives.length > 0 ? (
              viewMode === 'list' ? (
                // 列表视图
                filteredArchives.map(archive => (
                  <ArchiveItem key={`${archive.year}-${archive.month}`} archive={archive} />
                ))
              ) : (
                // 时间轴视图
                <div className="archive-timeline">
                  <Timeline mode="left">
                    {filteredArchives.map(archive => (
                      <Timeline.Item 
                        key={`${archive.year}-${archive.month}`}
                        dot={<div className="timeline-dot" />}
                      >
                        <div className="timeline-content">
                          <div className="timeline-year">
                            {archive.year}年{archive.month}月
                            <span className="timeline-count">{archive.count}篇</span>
                          </div>
                          
                          <div className="timeline-posts">
                            {archive.posts.map(post => (
                              <a 
                                key={post.id} 
                                href={`/post/${post.id}`}
                                className="timeline-post"
                              >
                                <Text strong style={{ flex: 1 }}>{post.title}</Text>
                                <span className="post-date">
                                  {new Date(post.date).toLocaleDateString()}
                                </span>
                                <div className="post-tags">
                                  {post.tags.slice(0, 2).map(tag => (
                                    <Tag key={tag} className="tag-item" color="blue">
                                      {tag}
                                    </Tag>
                                  ))}
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              )
            ) : (
              <div className="empty-archive">
                <Empty 
                  description={
                    <Text type="secondary">
                      没有找到符合条件的文章
                    </Text>
                  }
                />
                <Button 
                  type="primary" 
                  onClick={handleClearFilters}
                  style={{ marginTop: '16px' }}
                >
                  显示所有文章
                </Button>
              </div>
            )}
          </div>
        </Card>
      </Content>
    </Layout>
  );
};

export default ArchivePage;